import React, { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react'
import { refreshSettingsForDefaults } from 'common/util/defaultReminders'
import { Box, Paper, Typography, makeStyles } from '@material-ui/core'
import { actions } from 'reducers/applicationReducer'
import { bindActionCreators } from 'redux'
import { actions as takerActions } from '../../../reducers/minuteTakerReducer'
import { RouteComponentProps, withRouter } from 'react-router'
import { connect } from 'react-redux'
import takerSelectors from '../../../selectors/minuteTakerSelectors'
import actionEditorSelectors from '../../../selectors/actionEditorSelectors'
import { actions as editorActions } from '../../../reducers/actionEditorReducer'
import RightDrawer from './RightDrawer'
import { generateMeetingData } from './InvitationView/util'
import { getColumnSchema, OverlayNoRowsTemplate } from './ColumnComponents'
import { translate } from 'components/shared/internationalization'
import { ColDef } from 'ag-grid-community'
import { CustomSortIconsObject } from 'assets/icons/CustomSortIcon'
import { ActionData, CurrentMinutesProps, Details } from 'Types'
import LinearIndeterminate from 'components/shared/LinearProgress'
import i18n from 'common/util/internationalization'
import { getSessionStorageItem } from 'businesslayer/minutesLocalStore'
import { getAction } from 'businesslayer/networkrequests/actions/actions'
import { useActionDeepLink } from 'common/customHooks/useActionDeepLink'
import { addUTMParams, getActionDeepLinkUTMs, logActionDeepLinkAccess } from 'common/util/utmUtils'
import { useMinutesManager } from 'pages/minutes-manager-page/use-minutes-manager'
import blockingOperation from 'businesslayer/blockingOperation'
const GridComponent = lazy(() => import('components/shared/table/AgGrid'))

type Props = {
    actions: {
        handleActionStatusChanged: (...args: any[]) => void
        selectActionItem: (...args: any[]) => void
        saveExistingAction: (...args: any[]) => any
        updateTakerListAction: (...args: any[]) => void
    }
    savedAction: any
    minutesActions: Array<any>
    minutesSections: Array<any>
    dateFormat: string
    selectedActionItem: Details
    currentMinuteItem: CurrentMinutesProps
    committeeId: any
    onDocumentStatusChange: (status: string) => void
    showSideBar: boolean
    saveComplete: any
    setIsLoading: any
} & RouteComponentProps

const useStyles = makeStyles(() => ({
    gridConatainer: {
        padding: '24px',
        gap: '8px',
        maxHeight: '68vh',
        overflow: 'auto'
    },
    headerText: {
        fontFamily: 'Source Sans Pro',
        fontSize: '16px',
        fontWeight: 400,
        lineHeight: '20px',
        textAlign: 'left',
        color: '#1E1E1E',
        width: '352px',
        height: '35px',
        gap: '0px',
        textWrap: 'nowrap'
    },
    customHeader: {
        backgroundColor: '#EBEEF2'
    }
}))

const MinutesActions: React.FC<Props> = (props) => {
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const classes = useStyles()
    const [selectedAction, setSelectedAction] = useState<Details | null>(null)
    const committeeId = getSessionStorageItem('currentCommitteeId')
    const [lastOpenedActionId, setLastOpenedActionId] = useState<string | null>(null)
    const { isAdminOfCommittee } = useMinutesManager()

    const { handleDeepLink } = useActionDeepLink({
        setSelectedAction,
        setIsOpen
    })

    useEffect(
        () => {
            const url = new URL(window.location.href)
            const actionId = url.searchParams.get('action')
            if (actionId && props.minutesActions) {
                if (props.minutesActions && lastOpenedActionId !== actionId) {
                    handleDeepLink({ id: actionId })
                    setLastOpenedActionId(actionId)
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.minutesActions, lastOpenedActionId, handleDeepLink]
    )
    useEffect(
        () => {
            const url = new URL(window.location.href)
            if (selectedAction && isOpen) {
                url.pathname = `/taker/${props.currentMinuteItem.id}`
                url.searchParams.set('action', selectedAction.id)
                const utmParams = getActionDeepLinkUTMs('minutes', selectedAction.id)
                addUTMParams(url, utmParams)
                logActionDeepLinkAccess('minutes', selectedAction.id)
                window.history.replaceState(null, '', url.toString())
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isOpen, selectedAction, props.currentMinuteItem.id]
    )

    const handleEdit = useCallback(
        async (params: Details) => {
            refreshSettingsForDefaults()

            params.committee_id = committeeId
            setSelectedAction({
                ...params,
                reminders: []
            } as Details)
            setLastOpenedActionId(params.id)
            blockingOperation.showProgress(null, true)
            try {
                const response = await getAction(params.id)
                const actionData = response.data
                const updatedParams = {
                    ...params,
                    text: actionData.attributes.text,
                    notes: actionData.attributes.note,
                    notificationStatus: actionData.attributes.notification_status,
                    completionStatus: actionData.attributes.completion_status,
                    dueDate: actionData.attributes.due_date,
                    due_date: actionData.attributes.due_date,
                    assignees: actionData.attributes.assignees,
                    minutes_section_id: actionData.attributes.minutes_section_id,
                    minutesSectionId: actionData.attributes.minutes_section_id,
                    status: actionData.attributes.status,
                    action_completed: actionData.attributes.action_completed,
                    completion_status: actionData.attributes.completion_status,
                    completed_date: actionData.attributes.completed_date,
                    reminders: actionData.attributes.reminders || [],
                    remember_reminder_settings:
                        actionData.attributes.remember_reminder_settings || false
                }

                setSelectedAction(updatedParams)
                if (isOpen) {
                    const url = new URL(window.location.href)
                    url.searchParams.set('action', params.id)
                    const utmParams = getActionDeepLinkUTMs('minutes', params.id)
                    addUTMParams(url, utmParams)
                    window.history.replaceState(null, '', url.toString())
                }
                if (!isOpen || selectedAction?.id !== params.id) {
                    setIsOpen(true)
                }
            } catch (error) {
                console.error('Error fetching action:', error)
                setSelectedAction(params)
            } finally {
                blockingOperation.hideProgress()
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setSelectedAction, setIsOpen, committeeId, props, isOpen, selectedAction]
    )
    const columns = useMemo(
        () => getColumnSchema(classes, handleEdit),
        [classes, handleEdit]
    ) as ColDef[]
    const { committeeName } = props.currentMinuteItem.attributes

    const handleNotificationStatusChange = useCallback(
        (data) => {
            const keys = Object.keys(data.actionItems)[0]
            const updatedData = data.actionItems[keys]
            if (selectedAction?.id && updatedData) {
                setSelectedAction((prev) => ({
                    ...(prev ?? ({} as any)),
                    notificationStatus: updatedData?.attributes?.notificationStatus
                }))
            }
        },
        [selectedAction]
    )

    const data: ActionData[] = useMemo(
        () => generateMeetingData(props.minutesActions, committeeName, props.minutesSections),
        [props, committeeName]
    )

    const RightDrawerMemo = useMemo(
        () => (
            <RightDrawer
                setIsOpen={setIsOpen}
                isOpen={isOpen}
                selectedAction={selectedAction}
                setSelectedAction={setSelectedAction}
                handleNotificationStatusChange={handleNotificationStatusChange}
                sections={props.minutesSections}
            />
        ),
        [isOpen, selectedAction, handleNotificationStatusChange, props.minutesSections] //, handleDrawerClose
    )
    const gridOptions = {
        headerClass: classes.customHeader,
        icons: {
            ...CustomSortIconsObject
        },
        headerHeight: 45,
        enableColResize: true
    }
    const handleCellValueChanged = useCallback(
        (params: { data: Details }) => {
            props.setIsLoading(true)

            // Add isAdmin flag to the payload for saveExistingAction
            const isAdmin = isAdminOfCommittee(committeeId ?? '')
            const actionWithAdminFlag = { ...params.data, isAdmin }

            props.actions
                .saveExistingAction(actionWithAdminFlag)
                .then(() => {
                    props.setIsLoading(false)
                })
                .catch(() => {
                    props.setIsLoading(false)
                })
        },
        [props, isAdminOfCommittee, committeeId]
    )

    return (
        <Box>
            {RightDrawerMemo}
            <Suspense fallback={<LinearIndeterminate loading={true} />}>
                <Box id="minutes-actions-scroll" className={classes.gridConatainer}>
                    <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography
                            aria-label={i18n.t('MINUTES_ACTION_HEADER')}
                            className={classes.headerText}
                        >
                            {translate('MINUTES_ACTION_HEADER')}
                        </Typography>
                    </Box>
                    <Paper elevation={4}>
                        <GridComponent
                            gridId="minutes-actions"
                            columns={columns}
                            data={data}
                            gridOptions={gridOptions}
                            onCellValueChanged={handleCellValueChanged}
                            triggerColumnResizing={!props.showSideBar}
                            OverlayNoRowsTemplate={OverlayNoRowsTemplate}
                        />
                    </Paper>
                </Box>
            </Suspense>
        </Box>
    )
}

const mapStateToProps = (state: any, _: any) => ({
    minutesActions: takerSelectors.minutesActions(state.minuteTakerReducer),
    minutesSections: takerSelectors.minutesSections(state.minuteTakerReducer),
    dateFormat: takerSelectors.dateFormat(state.minuteTakerReducer),
    selectedActionItem: takerSelectors.selectedActionItem(state.minuteTakerReducer),
    currentMinuteItem: takerSelectors.currentMinuteItem(state.minuteTakerReducer),
    committeeId: takerSelectors.committeeId(state.minuteTakerReducer),
    savedAction: actionEditorSelectors.savedAction(state.actionEditorReducer)
})

const mapDispatchToProps = (dispatch: any) => {
    const { selectActionItem, saveExistingAction, updateTakerListAction } = {
        ...actions,
        ...takerActions,
        ...editorActions
    }

    return {
        actions: bindActionCreators(
            {
                selectActionItem,
                saveExistingAction,
                updateTakerListAction
            },
            dispatch
        )
    }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(MinutesActions))
